import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  FileText, Save, RotateCcw, Eye, Users, Plane, Gift, 
  ShoppingCart, Cake, RefreshCw, Check, X, Edit2
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from './admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_INFO = {
  during_stay: { label: 'Durante Soggiorno', icon: Users, color: 'green' },
  pre_arrival: { label: 'Pre-Arrivo', icon: Plane, color: 'blue' },
  post_stay: { label: 'Post-Soggiorno', icon: Gift, color: 'orange' },
  recovery: { label: 'Recupero', icon: ShoppingCart, color: 'red' },
  birthday: { label: 'Compleanno', icon: Cake, color: 'purple' }
};

const NotificationTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [byCategory, setByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('during_stay');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/notification-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
        setByCategory(data.by_category || {});
      }
    } catch (err) {
      console.error(err);
      toast.error('Errore nel caricamento dei template');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleEdit = (template) => {
    setEditingTemplate({
      ...template,
      title_it: template.title_it || '',
      title_en: template.title_en || '',
      body_it: template.body_it || '',
      body_en: template.body_en || ''
    });
  };

  const handleSave = async () => {
    if (!editingTemplate) return;
    setSaving(true);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/notification-templates/${editingTemplate.key}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title_it: editingTemplate.title_it,
          title_en: editingTemplate.title_en,
          body_it: editingTemplate.body_it,
          body_en: editingTemplate.body_en
        })
      });

      if (res.ok) {
        toast.success('Template salvato!');
        setEditingTemplate(null);
        fetchTemplates();
      } else {
        toast.error('Errore nel salvataggio');
      }
    } catch (err) {
      toast.error('Errore nel salvataggio');
    }
    setSaving(false);
  };

  const handleReset = async (key) => {
    if (!confirm('Ripristinare il template ai valori default?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/notification-templates/${key}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Template ripristinato');
        fetchTemplates();
      }
    } catch (err) {
      toast.error('Errore nel ripristino');
    }
  };

  const handlePreview = async (key, language = 'it') => {
    try {
      const res = await fetch(`${API_URL}/api/admin/notification-templates/preview?key=${key}&language=${language}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      if (res.ok) {
        const data = await res.json();
        setPreviewData(data);
      }
    } catch (err) {
      toast.error('Errore nella preview');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[#1B4332]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1B4332]">Template Notifiche Push</h1>
            <p className="text-gray-500 mt-1">Personalizza i testi delle notifiche automatiche</p>
          </div>
          <Button variant="outline" onClick={fetchTemplates}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Aggiorna
          </Button>
        </div>

        {/* Tabs per categoria */}
        <Tabs value={activeCategory} onValueChange={setActiveCategory}>
          <TabsList className="grid w-full grid-cols-5">
            {Object.entries(CATEGORY_INFO).map(([key, info]) => {
              const Icon = info.icon;
              return (
                <TabsTrigger key={key} value={key} className="flex items-center gap-1">
                  <Icon className="w-4 h-4" />
                  <span className="hidden md:inline">{info.label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {Object.entries(CATEGORY_INFO).map(([category, info]) => (
            <TabsContent key={category} value={category}>
              <div className="space-y-4">
                {(byCategory[category] || []).map((template) => (
                  <Card key={template.key} className={template.is_custom ? 'border-green-300 bg-green-50/30' : ''}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <FileText className="w-5 h-5 text-[#1B4332]" />
                            {template.name}
                            {template.is_custom && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                Personalizzato
                              </span>
                            )}
                          </CardTitle>
                          <CardDescription className="text-xs font-mono mt-1">
                            {template.key}
                          </CardDescription>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handlePreview(template.key)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleEdit(template)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          {template.is_custom && (
                            <Button size="sm" variant="outline" onClick={() => handleReset(template.key)}>
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <div className="text-xs text-gray-500 mb-1">🇮🇹 Italiano</div>
                          <div className="font-medium text-sm">{template.title_it}</div>
                          <div className="text-sm text-gray-600 mt-1">{template.body_it}</div>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <div className="text-xs text-gray-500 mb-1">🇬🇧 English</div>
                          <div className="font-medium text-sm">{template.title_en}</div>
                          <div className="text-sm text-gray-600 mt-1">{template.body_en}</div>
                        </div>
                      </div>
                      {template.variables && template.variables.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1">
                          <span className="text-xs text-gray-500">Variabili:</span>
                          {template.variables.map((v) => (
                            <code key={v} className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                              {`{{${v}}}`}
                            </code>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}

                {(!byCategory[category] || byCategory[category].length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    Nessun template in questa categoria
                  </div>
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Edit Dialog */}
        <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Modifica Template: {editingTemplate?.name}</DialogTitle>
            </DialogHeader>
            {editingTemplate && (
              <div className="space-y-4 py-4">
                {editingTemplate.variables?.length > 0 && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <div className="text-sm font-medium text-blue-800 mb-1">Variabili disponibili:</div>
                    <div className="flex flex-wrap gap-1">
                      {editingTemplate.variables.map((v) => (
                        <code key={v} className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded cursor-pointer hover:bg-blue-200">
                          {`{{${v}}}`}
                        </code>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  {/* Italiano */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 font-medium">
                      <span>🇮🇹</span> Italiano
                    </div>
                    <div>
                      <Label>Titolo</Label>
                      <Input
                        value={editingTemplate.title_it}
                        onChange={(e) => setEditingTemplate(prev => ({ ...prev, title_it: e.target.value }))}
                        placeholder="Titolo notifica"
                      />
                    </div>
                    <div>
                      <Label>Messaggio</Label>
                      <Textarea
                        value={editingTemplate.body_it}
                        onChange={(e) => setEditingTemplate(prev => ({ ...prev, body_it: e.target.value }))}
                        placeholder="Corpo della notifica"
                        rows={3}
                      />
                    </div>
                  </div>

                  {/* English */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 font-medium">
                      <span>🇬🇧</span> English
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={editingTemplate.title_en}
                        onChange={(e) => setEditingTemplate(prev => ({ ...prev, title_en: e.target.value }))}
                        placeholder="Notification title"
                      />
                    </div>
                    <div>
                      <Label>Message</Label>
                      <Textarea
                        value={editingTemplate.body_en}
                        onChange={(e) => setEditingTemplate(prev => ({ ...prev, body_en: e.target.value }))}
                        placeholder="Notification body"
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingTemplate(null)}>
                <X className="w-4 h-4 mr-2" />
                Annulla
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#1B4332] text-white">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Salvataggio...' : 'Salva'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={!!previewData} onOpenChange={() => setPreviewData(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Anteprima Notifica</DialogTitle>
            </DialogHeader>
            {previewData && (
              <div className="space-y-4 py-4">
                <div className="flex gap-2 mb-4">
                  <Button 
                    size="sm" 
                    variant={previewData.language === 'it' ? 'default' : 'outline'}
                    onClick={() => handlePreview(previewData.key, 'it')}
                  >
                    🇮🇹 Italiano
                  </Button>
                  <Button 
                    size="sm" 
                    variant={previewData.language === 'en' ? 'default' : 'outline'}
                    onClick={() => handlePreview(previewData.key, 'en')}
                  >
                    🇬🇧 English
                  </Button>
                </div>

                <div className="bg-gray-900 text-white rounded-xl p-4 max-w-sm mx-auto">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#1B4332] rounded-lg flex items-center justify-center text-white font-bold">
                      M
                    </div>
                    <div className="flex-1">
                      <div className="text-xs text-gray-400 mb-1">LA MAISONETTE</div>
                      <div className="font-semibold">{previewData.title}</div>
                      <div className="text-sm text-gray-300 mt-1">{previewData.body}</div>
                    </div>
                  </div>
                </div>

                {previewData.variables_used?.length > 0 && (
                  <div className="text-xs text-gray-500 text-center">
                    Variabili usate: {previewData.variables_used.join(', ')}
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => setPreviewData(null)}>Chiudi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default NotificationTemplatesPage;
